import { useCallback, useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import { useNavigate, type NavigateFunction } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useInstitution } from '@/contexts/InstitutionContext'
import { useRaspadinhaCampaigns } from '@/contexts/RaspadinhaCampaignsContext'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { canManageInstitutionCampaigns } from '@/lib/campaign-totp-step-up'
import { canViewCampaignDashboard } from '@/lib/collaborator-permissions'
import type { RaspadinhaCampaign, RaspadinhaCampaignStatus } from '@/types/raspadinha-campaign'
import listStyles from '../CampanhasPage/CampanhasPage.module.css'

const statusLabels: Record<RaspadinhaCampaignStatus, string> = {
  draft: 'Rascunho',
  active: 'Ativa',
  finished: 'Finalizada',
  cancelled: 'Cancelada',
}

const statusIcons: Record<RaspadinhaCampaignStatus, string> = {
  draft: 'mdi:pencil-outline',
  active: 'mdi:check-circle-outline',
  finished: 'mdi:flag-checkered',
  cancelled: 'mdi:close-circle-outline',
}

function formatDate(value?: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('pt-BR')
}

function getStatusLabel(status: RaspadinhaCampaignStatus): string {
  return statusLabels[status]
}

function getStatusIcon(status: RaspadinhaCampaignStatus): string {
  return statusIcons[status]
}

function getStatusClass(status: RaspadinhaCampaignStatus): string {
  const classes: Record<RaspadinhaCampaignStatus, string> = {
    draft: listStyles.statusDraft,
    active: listStyles.statusActive,
    finished: listStyles.statusFinished,
    cancelled: listStyles.statusCancelled,
  }
  return classes[status]
}

type RaspadinhaCampaignCardProps = {
  campaign: RaspadinhaCampaign
  listKind: 'main' | 'hidden'
  canManage: boolean
  canViewDashboard: boolean
  navigate: NavigateFunction
  toggleHiddenBusyId: number | null
  onSetHiddenFromMain: (campaignId: number, hidden: boolean) => void
}

function RaspadinhaCampaignCard({
  campaign,
  listKind,
  canManage,
  canViewDashboard,
  navigate,
  toggleHiddenBusyId,
  onSetHiddenFromMain,
}: RaspadinhaCampaignCardProps) {
  return (
    <div className={listStyles.campaignCard}>
      <div className={listStyles.campaignCardHeader}>
        <div className={listStyles.campaignIcon}>
          <Icon icon="mdi:ticket-confirmation-outline" />
        </div>
        <span className={`${listStyles.campaignStatus} ${getStatusClass(campaign.status)}`}>
          <Icon icon={getStatusIcon(campaign.status)} />
          {getStatusLabel(campaign.status)}
        </span>
      </div>

      <div className={listStyles.campaignCardBody}>
        <h4 className={listStyles.campaignName}>{campaign.name}</h4>
        <span className={listStyles.campaignSlug}>{campaign.slug || 'sem identificador'}</span>
        {campaign.is_global_example ? (
          <span className={`${listStyles.campaignStatus} ${listStyles.statusDraft}`}>
            Somente leitura
          </span>
        ) : null}
      </div>

      <div className={listStyles.campaignCardDetails}>
        <div className={listStyles.campaignMetaGrid}>
          <div className={listStyles.metaCard}>
            <span className={listStyles.metaLabel}>Início</span>
            <div className={listStyles.metaItem}>
              <Icon icon="mdi:calendar-start" />
              <strong>{formatDate(campaign.starts_at)}</strong>
            </div>
          </div>
          <div className={listStyles.metaCard}>
            <span className={listStyles.metaLabel}>Encerramento</span>
            <div className={listStyles.metaItem}>
              <Icon icon="mdi:calendar-end" />
              <strong>{formatDate(campaign.ends_at)}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className={listStyles.campaignCardActions}>
        <div className={listStyles.campaignCardActionsPrimary}>
          {campaign.slug && campaign.status === 'active' ? (
            <a
              className={`${listStyles.actionBtn} ${listStyles.actionBtnPrimary}`}
              href={`/raspadinha/${campaign.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title="Abrir página pública da campanha raspadinha"
            >
              <Icon icon="mdi:eye" aria-hidden />
              Visualizar
            </a>
          ) : (
            <button
              className={`${listStyles.actionBtn} ${listStyles.actionBtnPrimary}`}
              type="button"
              disabled
              title={
                !campaign.slug
                  ? 'Configure o slug na edição para visualizar'
                  : 'Disponível na página pública apenas com status Ativa'
              }
            >
              <Icon icon="mdi:eye" aria-hidden />
              Visualizar
            </button>
          )}
          {canViewDashboard ? (
            <button
              className={`${listStyles.actionBtn} ${listStyles.actionBtnAccent}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/campanha-raspadinha/dashboard?id=${campaign.id}`)
              }}
            >
              <Icon icon="mdi:chart-box-outline" aria-hidden />
              Dashboard
            </button>
          ) : null}
          {canManage ? (
            <button
              className={`${listStyles.actionBtn} ${listStyles.actionBtnAccent}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/campanha-raspadinha/editar?id=${campaign.id}`)
              }}
            >
              <Icon icon="mdi:pencil" aria-hidden />
              Entrar
            </button>
          ) : null}
        </div>
        {canManage ? (
          <div className={listStyles.campaignCardActionsRow}>
            {listKind === 'main' ? (
              <button
                className={listStyles.actionBtn}
                type="button"
                disabled={toggleHiddenBusyId === campaign.id || campaign.is_global_example}
                onClick={(e) => {
                  e.stopPropagation()
                  onSetHiddenFromMain(campaign.id, true)
                }}
                title={
                  campaign.is_global_example
                    ? 'Campanha de exemplo global não pode ser ocultada'
                    : 'Remove dos seletores e desta listagem principal'
                }
              >
                <Icon icon="mdi:eye-off-outline" aria-hidden />
                Ocultar
              </button>
            ) : (
              <button
                className={listStyles.actionBtn}
                type="button"
                disabled={toggleHiddenBusyId === campaign.id}
                onClick={(e) => {
                  e.stopPropagation()
                  onSetHiddenFromMain(campaign.id, false)
                }}
                title="Volta a aparecer na listagem principal"
              >
                <Icon icon="mdi:eye-outline" aria-hidden />
                Restaurar
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function CampanhaRaspadinhaTab() {
  const { guard, isCollaborator, hasPermission } = useAuth()
  const { institutionId } = useInstitution()
  const navigate = useNavigate()
  const { campaigns, isLoading, error: listError, setHiddenFromMain } = useRaspadinhaCampaigns()
  const { ensureElevated, runWithStepUp } = useCampaignTotpStepUp()
  const canManage = canManageInstitutionCampaigns(guard, Boolean(isCollaborator))
  const canViewDashboard = canViewCampaignDashboard(Boolean(isCollaborator), hasPermission)

  const [showHiddenPanel, setShowHiddenPanel] = useState(false)
  const [toggleHiddenBusyId, setToggleHiddenBusyId] = useState<number | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const mainCampaigns = useMemo(
    () => campaigns.filter((campaign) => !campaign.hidden_from_main),
    [campaigns]
  )
  const hiddenCampaigns = useMemo(
    () => campaigns.filter((campaign) => campaign.hidden_from_main),
    [campaigns]
  )

  const listStats = useMemo(() => {
    const total = campaigns.length
    const active = campaigns.filter((campaign) => campaign.status === 'active').length
    const draft = campaigns.filter((campaign) => campaign.status === 'draft').length
    const finished = campaigns.filter((campaign) => campaign.status === 'finished').length
    const cancelled = campaigns.filter((campaign) => campaign.status === 'cancelled').length
    return { total, active, draft, finished, cancelled }
  }, [campaigns])

  const handleSetHiddenFromMain = useCallback(
    async (campaignId: number, hidden: boolean) => {
      if (!canManage) return
      const allowed = await ensureElevated()
      if (!allowed) return
      setToggleHiddenBusyId(campaignId)
      setErrorMessage(null)
      try {
        await runWithStepUp(() => setHiddenFromMain(campaignId, hidden))
      } catch {
        setErrorMessage(
          hidden
            ? 'Não foi possível ocultar a campanha raspadinha.'
            : 'Não foi possível restaurar a campanha raspadinha na lista principal.'
        )
      } finally {
        setToggleHiddenBusyId(null)
      }
    },
    [canManage, ensureElevated, runWithStepUp, setHiddenFromMain]
  )

  return (
    <div className={listStyles.page}>
      <div className={listStyles.heroCard}>
        <div className={listStyles.heroText}>
          <span className={listStyles.kicker}>Campanha Raspadinha</span>
          <h2 className={listStyles.heroTitle}>Gestão de campanhas raspadinha</h2>
          <p className={listStyles.heroSubtitle}>
            Gerencie campanhas raspadinha da instituição. Use Visualizar para abrir a página pública
            em <strong>/raspadinha/slug-da-campanha</strong>.
          </p>
          <div className={listStyles.statsRow}>
            <div className={listStyles.statItem}>
              <div className={listStyles.statIcon}>
                <Icon icon="mdi:folder-multiple" />
              </div>
              <div className={listStyles.statContent}>
                <span>Total</span>
                <strong>{listStats.total}</strong>
              </div>
            </div>
            <div className={listStyles.statItem}>
              <div className={`${listStyles.statIcon} ${listStyles.statIconActive}`}>
                <Icon icon="mdi:play-circle" />
              </div>
              <div className={listStyles.statContent}>
                <span>Ativas</span>
                <strong>{listStats.active}</strong>
              </div>
            </div>
            <div className={listStyles.statItem}>
              <div className={`${listStyles.statIcon} ${listStyles.statIconDraft}`}>
                <Icon icon="mdi:pencil" />
              </div>
              <div className={listStyles.statContent}>
                <span>Rascunhos</span>
                <strong>{listStats.draft}</strong>
              </div>
            </div>
            <div className={listStyles.statItem}>
              <div className={`${listStyles.statIcon} ${listStyles.statIconFinished}`}>
                <Icon icon="mdi:check-all" />
              </div>
              <div className={listStyles.statContent}>
                <span>Finalizadas</span>
                <strong>{listStats.finished}</strong>
              </div>
            </div>
            <div className={listStyles.statItem}>
              <div className={`${listStyles.statIcon} ${listStyles.statIconCancelled}`}>
                <Icon icon="mdi:close-circle" />
              </div>
              <div className={listStyles.statContent}>
                <span>Canceladas</span>
                <strong>{listStats.cancelled}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(listError ?? errorMessage) ? (
        <div className={listStyles.errorBanner} role="alert">
          <Icon icon="mdi:alert-circle" />
          {listError ?? errorMessage}
        </div>
      ) : null}

      <div className={listStyles.contentStack}>
        <section className={listStyles.panelCard}>
          <div className={listStyles.panelHeader}>
            <div>
              <h3 className={listStyles.sectionTitle}>Campanha raspadinha de exemplo</h3>
              <p className={listStyles.sectionSubtitle}>
                Campanha global de demonstração. O botão Visualizar abre a experiência pública
                padrão da campanha.
              </p>
            </div>
            {!isLoading &&
            !(guard === 'admin' && institutionId === null) &&
            campaigns.length > 0 &&
            hiddenCampaigns.length > 0 ? (
              <button
                type="button"
                className={listStyles.ghostButton}
                onClick={() => setShowHiddenPanel((previous) => !previous)}
              >
                {showHiddenPanel
                  ? 'Fechar campanhas ocultas'
                  : `Ver campanhas ocultas (${hiddenCampaigns.length})`}
              </button>
            ) : null}
          </div>

          {isLoading ? (
            <div className={listStyles.loadingState}>
              <div className={listStyles.loadingSpinner} />
              <span>Carregando campanhas raspadinha...</span>
            </div>
          ) : guard === 'admin' && institutionId === null ? (
            <div className={listStyles.emptyState}>
              <div className={listStyles.emptyIcon}>
                <Icon icon="mdi:office-building-outline" />
              </div>
              <h4>Selecione uma instituição</h4>
              <p>Selecione uma instituição no menu acima para ver as campanhas raspadinha.</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div className={listStyles.emptyState}>
              <div className={listStyles.emptyIcon}>
                <Icon icon="mdi:ticket-confirmation-outline" />
              </div>
              <h4>Nenhuma campanha raspadinha cadastrada</h4>
              <p>Comece criando sua primeira campanha raspadinha</p>
            </div>
          ) : mainCampaigns.length === 0 ? (
            <div className={listStyles.emptyState}>
              <div className={listStyles.emptyIcon}>
                <Icon icon="mdi:eye-off-outline" />
              </div>
              <h4>Nenhuma campanha na lista principal</h4>
              <p>
                Todas as campanhas estão ocultas. Use o botão acima para ver as ocultas e restaurar
                as que quiser.
              </p>
            </div>
          ) : (
            <div className={listStyles.campaignGrid}>
              {mainCampaigns.map((campaign) => (
                <RaspadinhaCampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  listKind="main"
                  canManage={canManage}
                  canViewDashboard={canViewDashboard}
                  navigate={navigate}
                  toggleHiddenBusyId={toggleHiddenBusyId}
                  onSetHiddenFromMain={handleSetHiddenFromMain}
                />
              ))}
            </div>
          )}
        </section>

        {!isLoading &&
        !(guard === 'admin' && institutionId === null) &&
        showHiddenPanel &&
        hiddenCampaigns.length > 0 ? (
          <section className={listStyles.panelCard}>
            <div className={listStyles.panelHeader}>
              <div>
                <h3 className={listStyles.sectionTitle}>Campanhas raspadinha ocultas</h3>
                <p className={listStyles.sectionSubtitle}>
                  Estas campanhas não aparecem na listagem principal até serem restauradas.
                </p>
              </div>
            </div>
            <div className={listStyles.campaignGrid}>
              {hiddenCampaigns.map((campaign) => (
                <RaspadinhaCampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  listKind="hidden"
                  canManage={canManage}
                  canViewDashboard={canViewDashboard}
                  navigate={navigate}
                  toggleHiddenBusyId={toggleHiddenBusyId}
                  onSetHiddenFromMain={handleSetHiddenFromMain}
                />
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  )
}
